/**
 * CategoriesScreen — top-level category management.
 *
 * Pixel-faithful port of `CategoriesScreen` in
 * `design/project/screens-misc.jsx`.
 *
 * Layout:
 *   - Header with back / 完成 toggle.
 *   - 支出 / 收入 segmented control.
 *   - List of main categories grouped by `kind`. Each main row expands to
 *     show its sub-categories. Tapping the main row body toggles expand;
 *     tapping the pencil icon opens `CategoryEditSheet` to edit it.
 *   - System categories (`is_system: true`) display a 「預設」 chip and are
 *     not deletable from the list (the edit sheet enforces this too).
 *   - 「+ 新增 主分類」 button at the bottom and 「+ 新增子分類」 inside each
 *     expanded group both open `CategoryEditSheet` in `create` mode.
 *
 * Drag-to-reorder behaviour (edit mode only — see change
 * `category-drag-reorder`):
 *   - In edit mode, each row renders a drag handle (⋮⋮) that activates a
 *     dnd-kit sortable. Outside edit mode, no drag handle is shown.
 *   - Reorder is restricted to siblings sharing the same `parent_id`.
 *     Main categories reorder among mains; sub-categories reorder
 *     within their parent only. Cross-parent moves are blocked at the
 *     SortableContext level (one context per parent group).
 *   - On drop, `sort_order` for the affected sibling group is recomputed
 *     using a sparse integer scheme (0, 100, 200, ...). One
 *     `update_category` op is enqueued per row whose `sort_order`
 *     actually changed; same-index drops are no-ops.
 *   - The local store is updated optimistically; OCC conflicts during
 *     sync surface via the existing `occ_conflict` notification path.
 *
 * This component is **presentational**: it accepts the categories list +
 * change handlers from the parent. The parent wires those handlers to the
 * store + sync queue. Keeping it presentational makes testing easy and
 * avoids leaking store coupling into a screen that's also used inside the
 * `/settings/cats` route stack.
 */

import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';

import Icon, { type IconName } from '@/components/common/Icon';
import type { CategoryKind, CategoryRow } from '@/lib/sheets/types';
import CategoryEditSheet, {
  type CategoryEditSheetSubmit,
} from './CategoryEditSheet';
import type { RefCheck } from '@/state/slices/categories';
import SortableCategoryList, {
  type SortableGroup,
} from './SortableCategoryList';
import SortableCategoryRow, { CategoryRowBody } from './SortableCategoryRow';
import type { ReorderChange } from './sortOrderMath';

export interface CategoriesScreenProps {
  categories: CategoryRow[];
  onClose?: () => void;
  /**
   * Build a refCheck for a particular category id. The parent injects the
   * implementation that queries transactions + recurring slices.
   */
  refCheckFor: (categoryId: string) => RefCheck;
  onCreate: (input: CategoryEditSheetSubmit) => Promise<void> | void;
  onUpdate: (id: string, input: CategoryEditSheetSubmit) => Promise<void> | void;
  onDelete: (id: string) => Promise<void> | void;
  /**
   * Called after a successful reorder drop in edit mode. The `parentId`
   * is `''` for the main-category group and the parent main's id for
   * sub-category groups. `changes` contains only rows whose
   * `sort_order` actually changed; the parent is responsible for
   * applying the optimistic local update + enqueueing one
   * `update_category` op per change carrying the row's current `rev`.
   */
  onReorder?: (parentId: string, changes: ReorderChange[]) => void;
}

interface EditingState {
  mode: 'create' | 'edit';
  initial?: CategoryRow;
  parent_id?: string;
  defaultKind?: CategoryKind;
}

function tokenToCss(token: string): string {
  if (token.startsWith('--')) return `var(${token})`;
  return token;
}

const PILL_BAR: CSSProperties = {
  display: 'inline-flex',
  background: 'var(--bg-raised)',
  padding: 3,
  borderRadius: 999,
  font: '500 14px var(--font-cjk)',
};

function PillBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: 0,
        background: active ? 'var(--bg-surface)' : 'transparent',
        padding: '6px 18px',
        borderRadius: 999,
        color: active ? 'var(--fg-1)' : 'var(--fg-2)',
        boxShadow: active ? 'var(--shadow-1)' : 'none',
        cursor: 'pointer',
        font: 'inherit',
      }}
    >
      {children}
    </button>
  );
}

export default function CategoriesScreen({
  categories,
  onClose,
  refCheckFor,
  onCreate,
  onUpdate,
  onDelete,
  onReorder,
}: CategoriesScreenProps): JSX.Element {
  const [mode, setMode] = useState<CategoryKind>('expense');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [toast, setToast] = useState<string | null>(null);

  const tryDelete = async (cat: CategoryRow): Promise<void> => {
    if (cat.is_system) {
      setToast(`「${cat.name}」是預設分類，無法刪除`);
      window.setTimeout(() => setToast(null), 1800);
      return;
    }
    const counts = await refCheckFor(cat.id)();
    if (counts.txCount > 0 || counts.recurringCount > 0) {
      setToast(
        `「${cat.name}」仍有 ${counts.txCount} 筆紀錄使用中，無法刪除`,
      );
      window.setTimeout(() => setToast(null), 1800);
      return;
    }
    await onDelete(cat.id);
    setToast(`已刪除「${cat.name}」`);
    window.setTimeout(() => setToast(null), 1800);
  };

  // Split into mains (parent_id empty) + sub map.
  const { mains, subsByParent } = useMemo(() => {
    const mainsArr: CategoryRow[] = [];
    const map = new Map<string, CategoryRow[]>();
    for (const c of categories) {
      if (c.deleted_at) continue;
      if (c.kind !== mode) continue;
      if (!c.parent_id) {
        mainsArr.push(c);
      } else {
        const list = map.get(c.parent_id);
        if (list) list.push(c);
        else map.set(c.parent_id, [c]);
      }
    }
    // Stable sort by sort_order then name.
    const cmp = (a: CategoryRow, b: CategoryRow): number => {
      if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
      return a.name.localeCompare(b.name);
    };
    mainsArr.sort(cmp);
    for (const arr of map.values()) arr.sort(cmp);
    return { mains: mainsArr, subsByParent: map };
  }, [categories, mode]);

  const toggleExpand = (id: string): void => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  return (
    <div style={{ minHeight: '100%', background: 'var(--cream-50)' }}>
      <div
        style={{
          padding: '16px 16px 10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="關閉"
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              background: 'transparent',
              border: 0,
              cursor: 'pointer',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Icon name="chevL" size={22} stroke="var(--fg-1)" />
          </button>
        ) : (
          <div style={{ width: 36 }} />
        )}
        <div style={{ font: '600 16px var(--font-cjk)', color: 'var(--fg-1)' }}>
          分類管理
        </div>
        <button
          type="button"
          onClick={() => setEditMode((v) => !v)}
          style={{
            padding: '6px 12px',
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
            font: '500 14px var(--font-cjk)',
            color: editMode ? 'var(--accent)' : 'var(--fg-2)',
          }}
        >
          {editMode ? '完成' : '編輯'}
        </button>
      </div>

      <div style={{ padding: '8px 20px 14px' }}>
        <div style={PILL_BAR}>
          <PillBtn active={mode === 'expense'} onClick={() => setMode('expense')}>
            支出
          </PillBtn>
          <PillBtn active={mode === 'income'} onClick={() => setMode('income')}>
            收入
          </PillBtn>
        </div>
      </div>

      <div style={{ padding: '0 16px 80px' }}>
        {editMode ? (
          <SortableCategoryList
            groups={buildSortableGroups({
              mains,
              subsByParent,
              expanded,
              onDeleteMain: (cat) => void tryDelete(cat),
              onDeleteSub: (cat) => void tryDelete(cat),
              addSub: (parent) =>
                setEditing({
                  mode: 'create',
                  parent_id: parent.id,
                  defaultKind: parent.kind,
                }),
            })}
            onReorder={(parentId, changes) => {
              if (onReorder) onReorder(parentId, changes);
            }}
          />
        ) : (
        <div
          style={{
            background: 'var(--bg-surface)',
            borderRadius: 16,
            boxShadow: 'var(--shadow-1)',
            overflow: 'hidden',
          }}
        >
          {mains.map((c, i) => {
            const isOpen = expanded.has(c.id);
            const subs = subsByParent.get(c.id) ?? [];
            const Ic = (c.icon || 'tag') as IconName;
            const showBorder = i < mains.length - 1 || isOpen;
            return (
              <div key={c.id}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 14px',
                    borderBottom: showBorder ? '0.5px solid var(--border-1)' : '0',
                  }}
                >
                  <div
                    aria-hidden="true"
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: tokenToCss(c.color_token),
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon value={Ic} size={18} stroke="#fff" />
                  </div>

                  <button
                    type="button"
                    onClick={() => toggleExpand(c.id)}
                    style={{
                      flex: 1,
                      textAlign: 'left',
                      background: 'transparent',
                      border: 0,
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    <div
                      style={{
                        font: '500 15px var(--font-cjk)',
                        color: 'var(--fg-1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      {c.name}
                      {c.is_system && (
                        <span
                          style={{
                            font: '500 10px var(--font-cjk)',
                            color: 'var(--matcha-700)',
                            background: 'rgba(126,139,90,0.14)',
                            padding: '1px 6px',
                            borderRadius: 999,
                          }}
                        >
                          預設
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        font: '400 12px var(--font-cjk)',
                        color: 'var(--fg-3)',
                        marginTop: 2,
                      }}
                    >
                      {subs.length} 個子分類
                    </div>
                  </button>

                  <button
                    type="button"
                    aria-label={`編輯 ${c.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditing({ mode: 'edit', initial: c });
                    }}
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 8,
                      background: 'transparent',
                      border: 0,
                      cursor: 'pointer',
                      display: 'grid',
                      placeItems: 'center',
                      color: 'var(--fg-3)',
                    }}
                  >
                    <Icon name="edit" size={15} stroke="var(--fg-3)" />
                  </button>
                  <div
                    aria-hidden="true"
                    style={{
                      width: 24,
                      height: 24,
                      display: 'grid',
                      placeItems: 'center',
                      color: 'var(--fg-3)',
                      transform: isOpen ? 'rotate(90deg)' : 'none',
                      transition: 'transform 0.18s var(--ease-standard)',
                    }}
                  >
                    <Icon name="chevR" size={16} stroke="var(--fg-3)" />
                  </div>
                </div>

                {isOpen && (
                  <div style={{ background: 'var(--cream-100)', padding: '4px 0' }}>
                    {subs.map((s, si) => {
                      const SIc = (s.icon || 'tag') as IconName;
                      return (
                        <div
                          key={s.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '9px 14px 9px 56px',
                            borderBottom:
                              si < subs.length - 1
                                ? '0.5px solid var(--border-1)'
                                : '0',
                          }}
                        >
                          <div
                            aria-hidden="true"
                            style={{
                              width: 26,
                              height: 26,
                              borderRadius: 7,
                              background: tokenToCss(c.color_token),
                              display: 'grid',
                              placeItems: 'center',
                              flexShrink: 0,
                              opacity: 0.85,
                            }}
                          >
                            <Icon value={SIc} size={13} stroke="#fff" />
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              setEditing({ mode: 'edit', initial: s })
                            }
                            style={{
                              flex: 1,
                              textAlign: 'left',
                              background: 'transparent',
                              border: 0,
                              cursor: 'pointer',
                              padding: 0,
                              font: '500 14px var(--font-cjk)',
                              color: 'var(--fg-2)',
                            }}
                          >
                            {s.name}
                          </button>
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() =>
                        setEditing({
                          mode: 'create',
                          parent_id: c.id,
                          defaultKind: c.kind,
                        })
                      }
                      style={{
                        width: '100%',
                        padding: '10px 14px 10px 56px',
                        textAlign: 'left',
                        background: 'transparent',
                        border: 0,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        color: 'var(--accent)',
                        font: '500 13px var(--font-cjk)',
                      }}
                    >
                      <Icon name="plus" size={13} stroke="var(--accent)" /> 新增子分類
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}

        {!editMode && (
          <button
            type="button"
            onClick={() => setEditing({ mode: 'create', defaultKind: mode })}
            style={{
              width: '100%',
              marginTop: 10,
              padding: '13px',
              background: 'var(--bg-surface)',
              border: '1px dashed var(--sand-300)',
              borderRadius: 14,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              color: 'var(--accent)',
              font: '500 14px var(--font-cjk)',
            }}
          >
            <Icon name="plus" size={16} stroke="var(--accent)" />
            新增{mode === 'expense' ? '支出' : '收入'}分類
          </button>
        )}

        <div
          style={{
            font: '400 11.5px var(--font-cjk)',
            color: 'var(--fg-3)',
            padding: '14px 6px 0',
            lineHeight: 1.55,
          }}
        >
          {editMode
            ? '點減號可刪除未使用的分類；拖曳右側 ⋮⋮ 重排同層分類。'
            : '點擊主分類可展開子分類，鉛筆 icon 編輯主分類。子分類可單獨編輯、排序、刪除。'}
        </div>
      </div>

      {editing && (
        <CategoryEditSheet
          mode={editing.mode}
          {...(editing.initial ? { initial: editing.initial } : {})}
          {...(editing.parent_id !== undefined
            ? { parent_id: editing.parent_id }
            : {})}
          {...(editing.defaultKind ? { defaultKind: editing.defaultKind } : {})}
          refCheck={
            editing.mode === 'edit' && editing.initial
              ? refCheckFor(editing.initial.id)
              : async () => ({ txCount: 0, recurringCount: 0 })
          }
          onSubmit={async (input) => {
            if (editing.mode === 'edit' && editing.initial) {
              await onUpdate(editing.initial.id, input);
            } else {
              await onCreate(input);
            }
            setEditing(null);
          }}
          {...(editing.mode === 'edit' && editing.initial
            ? {
                onDelete: async () => {
                  await onDelete(editing.initial!.id);
                  setEditing(null);
                },
              }
            : {})}
          onClose={() => setEditing(null)}
        />
      )}

      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'absolute',
            left: 16,
            right: 16,
            bottom: 30,
            zIndex: 70,
            background: 'var(--mocha-700)',
            color: 'var(--cream-50)',
            padding: '11px 14px',
            borderRadius: 14,
            font: '500 13px var(--font-cjk)',
            boxShadow: 'var(--shadow-3)',
            textAlign: 'center',
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

/**
 * Some seeded categories use icon names that aren't in our `IconName` union
 * (e.g. older `home` token). Coerce unknown values to `tag` rather than
 * crashing the render.
 */
/**
 * Build the SortableGroup[] passed to `<SortableCategoryList>` in edit
 * mode. Mains form one group; each currently-expanded main contributes
 * an additional sub-group (one SortableContext per parent_id) so
 * cross-parent moves are blocked by dnd-kit at the context boundary.
 *
 * Each group's `renderRow` wraps `<SortableCategoryRow>` so the drag
 * handle (with listeners) is the only drag-activating element; the row
 * body remains scroll-friendly on touch.
 */
function buildSortableGroups({
  mains,
  subsByParent,
  expanded,
  onDeleteMain,
  onDeleteSub,
  addSub,
}: {
  mains: CategoryRow[];
  subsByParent: Map<string, CategoryRow[]>;
  expanded: Set<string>;
  onDeleteMain: (cat: CategoryRow) => void;
  onDeleteSub: (cat: CategoryRow) => void;
  addSub: (parent: CategoryRow) => void;
}): SortableGroup[] {
  const groups: SortableGroup[] = [];

  // Main group: parentId === ''.
  groups.push({
    parentId: '',
    rows: mains,
    renderRow: (cat, groupDisabled) => {
      const subs = subsByParent.get(cat.id) ?? [];
      const node: ReactNode = (
        <SortableCategoryRow
          key={cat.id}
          category={cat}
          groupDisabled={groupDisabled}
        >
          <CategoryRowBody
            category={cat}
            subCount={subs.length}
            onDelete={cat.is_system ? undefined : () => onDeleteMain(cat)}
          />
        </SortableCategoryRow>
      );
      // If this main is expanded, render its sub-group inline after the
      // row. The sub-group is its own SortableContext (added below as a
      // separate group); we only emit the visual sub-list container
      // here, the dnd plumbing happens via the next group entry.
      return node;
    },
  });

  // One sub-group per expanded main (Decision: Cross-parent block).
  for (const main of mains) {
    if (!expanded.has(main.id)) continue;
    const subs = subsByParent.get(main.id) ?? [];
    if (subs.length === 0) {
      // Still render the "+ 新增子分類" footer so the user can add
      // subs in edit mode if they wish.
    }
    groups.push({
      parentId: main.id,
      rows: subs,
      renderRow: (sub, groupDisabled) => (
        <SortableCategoryRow
          key={sub.id}
          category={sub}
          groupDisabled={groupDisabled}
        >
          <CategoryRowBody
            category={sub}
            onDelete={sub.is_system ? undefined : () => onDeleteSub(sub)}
          />
        </SortableCategoryRow>
      ),
      footer: (
        <button
          type="button"
          onClick={() => addSub(main)}
          style={{
            width: '100%',
            padding: '10px 14px 10px 56px',
            textAlign: 'left',
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: 'var(--accent)',
            font: '500 13px var(--font-cjk)',
          }}
        >
          <Icon name="plus" size={13} stroke="var(--accent)" /> 新增子分類
        </button>
      ),
    });
  }

  return groups;
}
